import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { GitBranch, Lock, CreditCard, ServerCog, Handshake, Scale, Users } from 'lucide-react';

/**
 * Two arguments, in the order the reader in VISION.md §2 needs them.
 *
 * That reader is an investor or an acquirer, so the thesis comes first — why the
 * mechanism is worth anything — and the engineering follows as the diligence
 * answer to "is this real, and could we take it on?".
 *
 * VISION.md §12 governs what may be said here: every technical claim is checkable
 * in this repo, and nothing implies traction, because there is none. The previous
 * version of this file claimed background checks, vehicle inspections, cash
 * payments and "thousands of satisfied riders" — none of which were true, and any
 * one of which would have been found in ten minutes of diligence.
 */

/** The thesis. Arguments, not metrics — there are no metrics to report. */
const thesis = [
  {
    icon: <Handshake className="h-6 w-6" />,
    title: 'A fixed price leaves money on the table twice',
    body: 'When the platform sets one price, riders who would have paid more walk, and drivers who would have gone for less stay parked. A parley lets both sides find the number they were each already willing to accept — and the platform takes its cut of a trip that otherwise never happened.',
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: 'Price control is a supply-side lever',
    body: "Driver churn is the dominant recurring cost in this category, and incumbents pay it down with bonuses that never stop. Letting drivers set their own floor, rather than accept an algorithmic rate, is a reason to stay that doesn't recur as a line item.",
  },
  {
    icon: <Scale className="h-6 w-6" />,
    title: 'And the case against, stated plainly',
    body: 'Negotiation adds friction to a flow where incumbents compete on having none, and a two-sided market with no riders and no drivers has to solve both at once. What follows argues the mechanism is built and works. It does not argue the market has been won.',
  },
];

/**
 * Diligence evidence. Every claim here must stay checkable against this repo.
 * If a claim stops being true, delete it — do not soften it (VISION.md §12).
 */
const engineering = [
  {
    icon: <Lock className="h-7 w-7" />,
    title: 'RLS on every table',
    description:
      'All 14 tables enable row-level security, with 51 policies across them. No client holds a key that bypasses it.',
  },
  {
    icon: <ServerCog className="h-7 w-7" />,
    title: 'State transitions live on the server',
    description:
      'Booking, accepting and every negotiation step go through SECURITY DEFINER functions — not table writes a client could forge.',
  },
  {
    icon: <CreditCard className="h-7 w-7" />,
    title: 'The take rate is wired, not projected',
    description:
      'Stripe Connect with a 10% platform fee, manual capture so riders are authorised and not charged until the trip completes, and webhooks verified by constant-time HMAC.',
  },
  {
    icon: <GitBranch className="h-7 w-7" />,
    title: 'One codebase, web and native',
    description:
      'The React web app and the Expo app share auth, domain logic, data hooks and the Supabase client through four typed workspace packages.',
  },
];

const AboutSection = () => {
  return (
    <div className="bg-muted/30 py-16">
      <div className="container mx-auto px-4">
        {/* ---- The thesis, flagged as a thesis ---- */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">Why ParleyRide?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A thesis, not a track record — ParleyRide has no users, no trips and no revenue,
              so there is nothing here dressed up as traction. These are the arguments for the
              mechanism, including the one against it.
            </p>
          </div>

          <ol className="space-y-6 bg-background rounded-2xl border border-muted-foreground/20 p-8 md:p-10">
            {thesis.map((point, i) => (
              <li key={point.title} className="flex gap-4">
                <div className="flex-shrink-0 flex flex-col items-center gap-2">
                  <div className="p-2.5 bg-accent rounded-full text-ride-primary">
                    {point.icon}
                  </div>
                  {i < thesis.length - 1 && (
                    <div className="w-px flex-1 bg-muted-foreground/15" aria-hidden="true" />
                  )}
                </div>
                <div className="pb-1">
                  <h3 className="font-semibold mb-1.5">{point.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{point.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* ---- The diligence answer: it is actually built ---- */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">And it is built, not drawn</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The demo above runs on the same code described here. Every claim is checkable in
              the repository.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engineering.map((feature) => (
              <Card
                key={feature.title}
                className="border-none shadow-sm hover:shadow-md transition-shadow"
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="p-3 bg-accent rounded-full text-ride-primary mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ---- One CTA, to the one thing a visitor can actually do ---- */}
        <div className="mt-16 text-center">
          <div className="ride-gradient text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">See it work</h2>
            <p className="mb-6 opacity-90 max-w-2xl mx-auto">
              Book a trip, get an offer back, and haggle it down — with sample data, no account,
              and nothing leaving your browser.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-ride-primary shadow-lg hover:bg-white/90"
            >
              <Link to="/demo">Try the demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;
